import React, { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Container,
  Divider,
  FileInput,
  Grid,
  Group,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconChecks,
  IconClock,
  IconFileDescription,
  IconFileInvoice,
  IconReceipt2,
  IconScale,
  IconSearch,
  IconShieldCheck,
  IconUpload,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

const receiptCandidates = [
  {
    id: 'GRN-2026-0021',
    vendor: 'OfisDonanım Ltd.',
    requisition: 'PR-2026-0105',
    requisitionTitle: 'Yeni personel için çalışma ekipmanı talebi',
    department: 'Bilgi İşlem',
    receivedAt: '2026-03-20 10:30',
    receivedBy: 'Depo Sorumlusu',
    totalAmount: 98500,
    acceptedAmount: 13400,
    currency: 'TRY',
    status: 'READY_FOR_INVOICE',
    lines: [
      { lineNo: 2, description: 'Laptop Çantası', qty: 1, price: 3500 },
      { lineNo: 4, description: 'Kurulum Hizmeti', qty: 1, price: 9900 },
    ],
  },
  {
    id: 'GRN-2026-0023',
    vendor: 'Anadolu Endüstri',
    requisition: 'PR-2026-0111',
    requisitionTitle: 'Şantiye personeli koruyucu ekipman alımı',
    department: 'Şantiye Operasyon',
    receivedAt: '2026-03-21 09:15',
    receivedBy: 'Depo Sorumlusu',
    totalAmount: 28400,
    acceptedAmount: 25000,
    currency: 'TRY',
    status: 'PARTIAL_INVOICE',
    lines: [
      { lineNo: 1, description: 'Koruyucu Baret', qty: 30, price: 400 },
      { lineNo: 3, description: 'İş Eldiveni', qty: 50, price: 120 },
    ],
  },
];

const statusConfig = {
  DRAFT: { label: 'Taslak', color: 'gray' },
  CONTROL: { label: 'Kontrol Ediliyor', color: 'orange' },
  APPROVED: { label: 'Onaylandı', color: 'teal' },
  REJECTED: { label: 'Reddedildi', color: 'red' },
  READY_FOR_INVOICE: { label: 'Faturaya Hazır', color: 'blue' },
  PARTIAL_INVOICE: { label: 'Kısmi Faturalama', color: 'violet' },
};

function StatusBadge({ status }) {
  const s = statusConfig[status] || { label: status, color: 'gray' };
  return (
    <Badge color={s.color} variant="light" radius="sm">
      {s.label}
    </Badge>
  );
}

function SummaryCard({ title, value, subtitle, color, icon: Icon }) {
  return (
    <Card withBorder radius="lg" p="lg" shadow="sm">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Text size="sm" c="dimmed" fw={500}>
            {title}
          </Text>
          <Text fw={800} size="1.55rem" lh={1.1}>
            {value}
          </Text>
          <Text size="xs" c="dimmed">
            {subtitle}
          </Text>
        </Stack>
        <ThemeIcon color={color} variant="light" size={46} radius="md">
          <Icon size={22} />
        </ThemeIcon>
      </Group>
    </Card>
  );
}

function SectionCard({ title, description, children, action }) {
  return (
    <Paper withBorder radius="lg" p="lg" shadow="sm">
      <Group justify="space-between" align="flex-start" mb="md">
        <div>
          <Title order={4}>{title}</Title>
          {description ? (
            <Text size="sm" c="dimmed" mt={4}>
              {description}
            </Text>
          ) : null}
        </div>
        {action}
      </Group>
      {children}
    </Paper>
  );
}

function InfoRow({ label, value }) {
  return (
    <>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Text size="sm" c="dimmed">
          {label}
        </Text>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Text size="sm" fw={600} ta="right">
            {value || '-'}
          </Text>
        ) : (
          value
        )}
      </Group>
      <Divider />
    </>
  );
}

export default function ProcurementInvoiceManagementPage() {
  const navigate = useNavigate();

  const [selectedReceiptId, setSelectedReceiptId] = useState(receiptCandidates[0].id);
  const [search, setSearch] = useState('');
  const [invoicePdf, setInvoicePdf] = useState(null);

  const [invoiceHeader, setInvoiceHeader] = useState({
    invoiceNo: 'FAT-2026-0024',
    invoiceDate: '2026-03-22',
    taxRate: 20,
    currency: 'TRY',
    status: 'DRAFT',
    note: 'Mal kabul sonrası kabul edilen kalemlere ait fatura kaydı.',
  });

  const [invoiceLines, setInvoiceLines] = useState([
    { lineNo: 2, description: 'Laptop Çantası', qty: 1, unitPrice: 3500 },
    { lineNo: 4, description: 'Kurulum Hizmeti', qty: 1, unitPrice: 9900 },
  ]);

  const filteredReceipts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return receiptCandidates;
    return receiptCandidates.filter((item) => JSON.stringify(item).toLowerCase().includes(q));
  }, [search]);

  const selectedReceipt = useMemo(
    () => receiptCandidates.find((r) => r.id === selectedReceiptId),
    [selectedReceiptId]
  );

  const subtotal = useMemo(
    () => invoiceLines.reduce((t, l) => t + Number(l.qty || 0) * Number(l.unitPrice || 0), 0),
    [invoiceLines]
  );

  const taxAmount = useMemo(
    () => subtotal * (Number(invoiceHeader.taxRate || 0) / 100),
    [subtotal, invoiceHeader.taxRate]
  );

  const totalAmount = useMemo(() => subtotal + taxAmount, [subtotal, taxAmount]);

  const variance = useMemo(() => {
    if (!selectedReceipt) return 0;
    return subtotal - selectedReceipt.acceptedAmount;
  }, [subtotal, selectedReceipt]);

  const updateHeader = (field, val) =>
    setInvoiceHeader((prev) => ({ ...prev, [field]: val }));

  const updateLine = (lineNo, field, val) => {
    setInvoiceLines((prev) =>
      prev.map((l) => (l.lineNo === lineNo ? { ...l, [field]: val } : l))
    );
  };

  const stats = useMemo(() => {
    return {
      candidateCount: receiptCandidates.length,
      controlCount: 2,
      approvedCount: 4,
      totalAcceptedAmount: receiptCandidates.reduce((acc, item) => acc + item.acceptedAmount, 0),
    };
  }, []);

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Paper withBorder p="xl" radius="xl" shadow="sm">
          <Group justify="space-between">
            <Group>
              <Button
                variant="subtle"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate("/purchase-dashboard")}
              >
                Geri
              </Button>
              <Badge variant="light" color="blue">
                Statik Demo
              </Badge>
            </Group>
          </Group>

          <Title mt="md">Fatura Kayıt / Fatura Kontrol</Title>
          <Text c="dimmed" size="sm" mt="sm">
            Mal kabul sonrası kabul edilen kalemlerin faturalarının kaydedildiği, PDF yükleme ve
            finansal kontrolün yapıldığı ekran.
          </Text>

          <SimpleGrid cols={{ base: 1, md: 4 }} mt="lg" spacing="md">
            <SummaryCard
              title="Fatura Adayı"
              value={stats.candidateCount}
              subtitle="Faturalanabilir mal kabul kaydı"
              color="blue"
              icon={IconReceipt2}
            />
            <SummaryCard
              title="Kontrol Bekleyen"
              value={stats.controlCount}
              subtitle="Finansal inceleme bekleyen"
              color="orange"
              icon={IconClock}
            />
            <SummaryCard
              title="Onaylanan"
              value={stats.approvedCount}
              subtitle="Muhasebeye hazır kayıt"
              color="teal"
              icon={IconChecks}
            />
            <SummaryCard
              title="Kabul Tutarı"
              value={`${stats.totalAcceptedAmount} ₺`}
              subtitle="Toplam kabul edilmiş hacim"
              color="grape"
              icon={IconScale}
            />
          </SimpleGrid>
        </Paper>

        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, xl: 3.5 }}>
            <Stack gap="md">
              <SectionCard
                title="Fatura Aday Havuzu"
                description="Mal kabul üzerinden fatura oluşturulacak kaydı seçin."
              >
                <Stack gap="sm">
                  <TextInput
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    placeholder="GRN, tedarikçi, talep ara..."
                    leftSection={<IconSearch size={16} />}
                  />

                  <Stack gap="xs">
                    {filteredReceipts.map((r) => (
                      <Paper
                        key={r.id}
                        withBorder
                        p="sm"
                        radius="md"
                        onClick={() => setSelectedReceiptId(r.id)}
                        style={{
                          cursor: 'pointer',
                          borderColor:
                            selectedReceiptId === r.id
                              ? 'var(--mantine-color-blue-4)'
                              : undefined,
                          backgroundColor:
                            selectedReceiptId === r.id
                              ? 'var(--mantine-color-blue-0)'
                              : undefined,
                        }}
                      >
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          <div>
                            <Text fw={700} size="sm">
                              {r.id}
                            </Text>
                            <Text size="sm" mt={2}>
                              {r.vendor}
                            </Text>
                            <Text size="xs" c="dimmed" mt={4}>
                              {r.requisition}
                            </Text>
                          </div>

                          <Stack gap={6} align="flex-end">
                            <StatusBadge status={r.status} />
                            <Badge variant="light" color="gray">
                              {r.acceptedAmount} ₺
                            </Badge>
                          </Stack>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </Stack>
              </SectionCard>

              {selectedReceipt && (
                <SectionCard
                  title="Seçilen Kayıt Özeti"
                  description="Diğer sayfalardaki gibi sol panelde detay görünümü."
                >
                  <Stack gap="sm">
                    <InfoRow label="Mal Kabul No" value={selectedReceipt.id} />
                    <InfoRow label="Tedarikçi" value={selectedReceipt.vendor} />
                    <InfoRow label="Talep No" value={selectedReceipt.requisition} />
                    <InfoRow label="Talep Başlığı" value={selectedReceipt.requisitionTitle} />
                    <InfoRow label="Departman" value={selectedReceipt.department} />
                    <InfoRow label="Teslim Alan" value={selectedReceipt.receivedBy} />
                    <InfoRow label="Teslim Zamanı" value={selectedReceipt.receivedAt} />
                    <InfoRow label="Durum" value={<StatusBadge status={selectedReceipt.status} />} />
                    <InfoRow label="Toplam Tutar" value={`${selectedReceipt.totalAmount} ₺`} />
                    <InfoRow
                      label="Kabul Edilen Tutar"
                      value={`${selectedReceipt.acceptedAmount} ₺`}
                    />
                  </Stack>
                </SectionCard>
              )}

              <SectionCard
                title="Kontrol Notu"
                description="Bu ekran 3-way matching mantığına göre çalışır."
              >
                <Stack gap="xs">
                  <Group wrap="nowrap" align="flex-start">
                    <ThemeIcon color="blue" variant="light" mt={2}>
                      <IconFileDescription size={16} />
                    </ThemeIcon>
                    <Text size="sm" c="dimmed">
                      Talep / teklif / mal kabul hattındaki finansal tutarlılık burada kontrol edilir.
                    </Text>
                  </Group>
                  <Group wrap="nowrap" align="flex-start">
                    <ThemeIcon color="orange" variant="light" mt={2}>
                      <IconAlertTriangle size={16} />
                    </ThemeIcon>
                    <Text size="sm" c="dimmed">
                      Fatura tutarı ile kabul edilen tutar arasında fark varsa mutabakat farkı oluşur.
                    </Text>
                  </Group>
                </Stack>
              </SectionCard>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, xl: 8.5 }}>
            <Stack gap="md">
              <SectionCard title="Fatura Başlık Bilgileri" description="Fatura üst bilgilerinin girildiği alan.">
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <TextInput
                      label="Fatura No"
                      value={invoiceHeader.invoiceNo}
                      onChange={(e) => updateHeader('invoiceNo', e.target.value)}
                    />
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <TextInput
                      type="date"
                      label="Fatura Tarihi"
                      value={invoiceHeader.invoiceDate}
                      onChange={(e) => updateHeader('invoiceDate', e.target.value)}
                    />
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <Select
                      label="Para Birimi"
                      value={invoiceHeader.currency}
                      data={[
                        { value: 'TRY', label: 'TRY' },
                        { value: 'USD', label: 'USD' },
                        { value: 'EUR', label: 'EUR' },
                      ]}
                      onChange={(v) => updateHeader('currency', v)}
                    />
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <Select
                      label="Durum"
                      value={invoiceHeader.status}
                      data={[
                        { value: 'DRAFT', label: 'Taslak' },
                        { value: 'CONTROL', label: 'Kontrol Ediliyor' },
                        { value: 'APPROVED', label: 'Onaylandı' },
                        { value: 'REJECTED', label: 'Reddedildi' },
                      ]}
                      onChange={(v) => updateHeader('status', v)}
                    />
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, md: 3 }}>
                    <NumberInput
                      label="KDV %"
                      value={invoiceHeader.taxRate}
                      onChange={(v) => updateHeader('taxRate', Number(v) || 0)}
                    />
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, md: 9 }}>
                    <FileInput
                      label="Fatura PDF"
                      placeholder="PDF dosyası seçin"
                      accept="application/pdf"
                      value={invoicePdf}
                      onChange={setInvoicePdf}
                      leftSection={<IconUpload size={16} />}
                      clearable
                    />
                  </Grid.Col>

                  <Grid.Col span={12}>
                    <Textarea
                      label="Not"
                      value={invoiceHeader.note}
                      onChange={(e) => updateHeader('note', e.target.value)}
                      minRows={3}
                    />
                  </Grid.Col>
                </Grid>

                {invoicePdf && (
                  <Paper withBorder p="sm" radius="md" mt="md" bg="gray.0">
                    <Group justify="space-between" align="center">
                      <div>
                        <Text fw={600} size="sm">
                          Yüklenen Dosya
                        </Text>
                        <Text size="xs" c="dimmed">
                          {invoicePdf.name} • {Math.round(invoicePdf.size / 1024)} KB
                        </Text>
                      </div>
                      <Badge variant="light" color="blue">
                        PDF Hazır
                      </Badge>
                    </Group>
                  </Paper>
                )}
              </SectionCard>

              <SectionCard title="Fatura Satırları" description="Mal kabulden gelen satırların fatura satırına dönüştüğü alan.">
                <Table mt="md" striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Satır</Table.Th>
                      <Table.Th>Açıklama</Table.Th>
                      <Table.Th>Miktar</Table.Th>
                      <Table.Th>Birim Fiyat</Table.Th>
                      <Table.Th>Tutar</Table.Th>
                    </Table.Tr>
                  </Table.Thead>

                  <Table.Tbody>
                    {invoiceLines.map((line) => (
                      <Table.Tr key={line.lineNo}>
                        <Table.Td>{line.lineNo}</Table.Td>
                        <Table.Td>{line.description}</Table.Td>
                        <Table.Td>
                          <NumberInput
                            value={line.qty}
                            onChange={(v) => updateLine(line.lineNo, 'qty', Number(v) || 0)}
                            min={0}
                          />
                        </Table.Td>
                        <Table.Td>
                          <NumberInput
                            value={line.unitPrice}
                            onChange={(v) =>
                              updateLine(line.lineNo, 'unitPrice', Number(v) || 0)
                            }
                            min={0}
                          />
                        </Table.Td>
                        <Table.Td>{line.qty * line.unitPrice} ₺</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </SectionCard>

              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                <SectionCard title="Finansal Özet" description="Fatura hesapları ve kontrol toplamları.">
                  <Stack gap="sm">
                    <InfoRow label="Ara Toplam" value={`${subtotal} ₺`} />
                    <InfoRow label="KDV" value={`${taxAmount} ₺`} />
                    <InfoRow label="Genel Toplam" value={`${totalAmount} ₺`} />
                    <InfoRow
                      label="Mutabakat Farkı"
                      value={
                        <Badge color={variance === 0 ? 'teal' : 'orange'} variant="light">
                          {variance} ₺
                        </Badge>
                      }
                    />
                    <InfoRow label="Fatura Durumu" value={<StatusBadge status={invoiceHeader.status} />} />
                  </Stack>
                </SectionCard>

                <SectionCard title="Kaynak Satırlar" description="Seçilen mal kabulden gelen referans satırlar.">
                  <Stack gap="sm">
                    {selectedReceipt?.lines.map((line) => (
                      <Paper key={line.lineNo} withBorder p="sm" radius="md">
                        <Group justify="space-between" align="flex-start">
                          <div>
                            <Text fw={700} size="sm">
                              {line.lineNo}. {line.description}
                            </Text>
                            <Text size="xs" c="dimmed" mt={4}>
                              {line.qty} adet • {line.price} ₺
                            </Text>
                          </div>
                          <Badge variant="light">{line.qty * line.price} ₺</Badge>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </SectionCard>
              </SimpleGrid>

              <SectionCard title="Aksiyonlar" description="Fatura yaşam döngüsü aksiyonları.">
                <Group grow>
                  <Button leftSection={<IconFileInvoice size={16} />}>Fatura Kaydet</Button>
                  <Button color="orange" variant="light" leftSection={<IconSearch size={16} />}>
                    Kontrole Gönder
                  </Button>
                  <Button color="teal" variant="light" leftSection={<IconShieldCheck size={16} />}>
                    Onayla
                  </Button>
                  <Button color="red" variant="light" leftSection={<IconAlertTriangle size={16} />}>
                    Faturayı Reddet
                  </Button>
                </Group>
              </SectionCard>
            </Stack>
          </Grid.Col>
        </Grid>

        <Paper withBorder p="md" radius="lg" shadow="sm" style={{ borderStyle: 'dashed' }}>
          <Text size="sm" c="dimmed">
            Bu ekran artık diğer sayfalardaki yapıya daha yakın: sol panelde seçim + detay, sağ panelde işlem alanı, ayrıca PDF yükleme ile gerçek kullanım senaryosuna daha yakın bir akış sunuyor.
          </Text>
        </Paper>
      </Stack>
    </Container>
  );
}